/**
 * Service-level embed query defaults + paste helpers.
 *
 * Per-song URLs stay full embed `src` values (including identity such as
 * SoundCloud’s nested `url=`). Service items may define `defaultEmbedQuery`
 * preference params (e.g. show_teaser=false&color=%23ff). The player merges
 * them so service keys win — no service-specific player code required.
 */

/** Query keys that identify the resource (never treat as service defaults). */
const IDENTITY_QUERY_KEYS = new Set(["url"]);

/**
 * Decode common HTML entities from iframe markup pastes.
 * @param {string} s
 * @returns {string}
 */
const decodeHtmlEntities = (s) =>
  String(s)
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">");

/**
 * Extract an embed `src` from a full iframe snippet or a bare URL.
 * @param {string|null|undefined} raw
 * @returns {string}
 */
export const extractEmbedSrc = (raw) => {
  if (raw == null) return "";
  const text = String(raw).trim();
  if (!text) return "";

  const quoted = text.match(/src\s*=\s*["']([^"']+)["']/i);
  if (quoted?.[1]) return decodeHtmlEntities(quoted[1].trim());

  const unquoted = text.match(/src\s*=\s*([^\s>]+)/i);
  if (unquoted?.[1]) {
    return decodeHtmlEntities(unquoted[1].replace(/\/?>$/, "").trim());
  }

  // Bare URL (or URL with surrounding whitespace / quotes)
  const bare = text.replace(/^['"]|['"]$/g, "").trim();
  if (/^https?:\/\//i.test(bare)) return bare;

  return "";
};

/**
 * Parse a query string, `?a=1&b=2`, or a full URL’s search into [key, value][].
 * @param {string|null|undefined} raw
 * @returns {Array<[string, string]>}
 */
export const parseQueryEntries = (raw) => {
  if (raw == null) return [];
  let text = String(raw).trim();
  if (!text) return [];

  // Full URL → use its search
  if (/^https?:\/\//i.test(text)) {
    try {
      return [...new URL(text).searchParams.entries()];
    } catch {
      const q = text.indexOf("?");
      text = q >= 0 ? text.slice(q + 1) : "";
    }
  }

  if (text.startsWith("?")) text = text.slice(1);
  if (!text) return [];

  try {
    return [...new URLSearchParams(text).entries()];
  } catch {
    return [];
  }
};

/**
 * Preference-only query string from a pasted embed / URL.
 * Drops identity keys (e.g. SoundCloud nested `url`) so service defaults
 * never pin every song to one track.
 *
 * @param {string|null|undefined} raw - iframe HTML, full src, or query string
 * @returns {string} e.g. "show_teaser=false&color=%23070d07"
 */
export const extractDefaultQueryFromEmbed = (raw) => {
  if (raw == null) return "";
  const text = String(raw).trim();
  if (!text) return "";

  const src = extractEmbedSrc(text);
  let entries;
  if (src) {
    entries = parseQueryEntries(src);
  } else if (text.includes("=") && !text.includes("<")) {
    // Already a query string (with or without leading ?)
    entries = parseQueryEntries(text);
  } else {
    return "";
  }

  const params = new URLSearchParams();
  entries.forEach(([key, value]) => {
    if (IDENTITY_QUERY_KEYS.has(String(key).toLowerCase())) return;
    params.append(key, value);
  });
  return params.toString();
};

/**
 * Merge service default query params onto a song/item embed URL.
 * Service keys overwrite matching keys on the item URL; other item keys keep.
 *
 * @param {string|null|undefined|false} baseUrl - per-item embed src
 * @param {string|null|undefined} defaultQuery - service defaultEmbedQuery
 * @returns {string|null|undefined|false}
 */
export const mergeEmbedUrl = (baseUrl, defaultQuery) => {
  if (baseUrl == null || baseUrl === false) return baseUrl;
  if (typeof baseUrl !== "string") return baseUrl;

  const trimmed = baseUrl.trim();
  if (!trimmed) return baseUrl;

  const defaults = parseQueryEntries(defaultQuery);
  if (defaults.length === 0) return trimmed;

  try {
    const u = new URL(trimmed);
    defaults.forEach(([key, value]) => {
      u.searchParams.set(key, value);
    });
    return u.toString();
  } catch {
    // Fallback: append/replace via string (rare non-standard URLs)
    try {
      const qIndex = trimmed.indexOf("?");
      const base = qIndex >= 0 ? trimmed.slice(0, qIndex) : trimmed;
      const existing = new URLSearchParams(
        qIndex >= 0 ? trimmed.slice(qIndex + 1) : "",
      );
      defaults.forEach(([key, value]) => {
        existing.set(key, value);
      });
      const qs = existing.toString();
      return qs ? `${base}?${qs}` : base;
    } catch {
      return trimmed;
    }
  }
};
